import React from 'react';
import { GameState, RoundMetrics } from './types';
import { getRoundInstructions, getInRoundInstruction } from './utils';
import ShapeRenderer from './ShapeRenderer';


// Countdown Component
const Countdown: React.FC<{ onComplete: () => void }> = ({ onComplete }) => {
  const [count, setCount] = React.useState(3);
  const onCompleteRef = React.useRef(onComplete);

  React.useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  React.useEffect(() => {
    if (count === 0) {
      onCompleteRef.current();
      return;
    }
    const timer = setTimeout(() => {
      setCount(count - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [count]);

  if (count === 0) return null;

  return (
    <div className="absolute inset-0 flex items-center justify-center z-50">
      <div className="text-9xl font-bold text-sleek-600">
        {count}
      </div>
    </div>
  );
};

interface GameUIProps {
  gameState: GameState;
  elapsedTime: number;
  stimulusRotation: number;
  roundMetrics?: RoundMetrics[];
  onStartGame: () => void;
  onStartRound: () => void;
  onStimulusClick: () => void;
  onScreenClick: (e: React.MouseEvent) => void;

  width?: string;
  height?: string;
}



const GameUI: React.FC<GameUIProps> = ({
  gameState,
  elapsedTime,
  stimulusRotation,
  roundMetrics = [],
  onStartGame,
  onStartRound,
  onStimulusClick,
  onScreenClick,

  width = "960px",
  height = "540px"
}) => {
  // Countdown state (must be top-level for hooks order)
  const [showCountdown, setShowCountdown] = React.useState(false);
  const [showRoundCountdown, setShowRoundCountdown] = React.useState(false);

  // Countdown handlers
  const handleStartClick = React.useCallback(() => {
    setShowCountdown(true);
  }, []);

  const handleCountdownComplete = React.useCallback(() => {
    setShowCountdown(false);
    onStartGame();
  }, [onStartGame]);

  const handleRoundStartClick = React.useCallback(() => {
    setShowRoundCountdown(true);
  }, []);

  const handleRoundCountdownComplete = React.useCallback(() => {
    setShowRoundCountdown(false);
    onStartRound();
  }, [onStartRound]);

  // Show initial instructions
  if (gameState.showInstructions) {

    return (
      <div className="w-full h-full bg-white rounded-lg shadow-lg" style={{ width: width, height: height }}>
        {/* Game Board Container - matches main game structure */}
        <div className="bg-gray-50 rounded-lg p-4 shadow-md relative" style={{
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          display: 'block',
          margin: '0 auto'
        }}>
          
          {/* Game Play Area - matches main game structure */}
          <div className="relative w-full h-full flex items-center justify-center" style={{ height: 'calc(100% - 60px)' }}>
            
            {/* Countdown Overlay */}
            {showCountdown && (
              <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                <Countdown onComplete={handleCountdownComplete} />
              </div>
            )}
            
            {/* Instructions Content */}
            <div className="flex flex-col justify-center items-center w-full max-w-2xl">
              <div className="text-center">
                <h2 className="font-extrabold text-3xl md:text-4xl text-sleek-700 mb-8 tracking-wide">How to Play</h2>
                <ul className="text-gray-800 space-y-4 text-lg list-disc list-inside px-0 mb-8">
                  <li>Watch for the <b>target shape and pattern</b> shown on the left</li>
                  <li>When a stimulus appears on the right, decide if it matches the target</li>
                  <li><b>Round 1:</b> Click if <b>shape OR pattern</b> matches</li>
                  <li><b>Round 2:</b> Click if <b>BOTH shape AND pattern</b> match</li>
                  <li><b>Round 3:</b> Click if <b>BOTH match</b> (shapes may be rotated)</li>
                  <li>Some trials may have <b>distractions</b> (visual effects) – stay focused!</li>
                </ul>
                <button
                  onClick={handleStartClick}
                  className="bg-gradient-to-r from-sleek-600 to-emerald-600 text-white text-xl px-12 py-4 rounded-full font-bold shadow hover:from-sleek-700 hover:to-emerald-700 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-emerald-300"
                >
                  Start Game
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show round start screen
  if (gameState.showRoundStart) {
    const instructions = getRoundInstructions(gameState.currentRound);

    return (
      <div className="w-full h-full bg-white rounded-lg shadow-lg" style={{ width: width, height: height }}>
        {/* Game Board Container - matches main game structure */}
        <div className="bg-gray-50 rounded-lg p-4 shadow-md relative" style={{
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          display: 'block',
          margin: '0 auto'
        }}>
          
          {/* Game Play Area - matches main game structure */}
          <div className="relative w-full h-full" style={{ height: 'calc(100% - 60px)' }}>
            
            {/* Countdown Overlay */}
            {showRoundCountdown && (
              <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                <Countdown onComplete={handleRoundCountdownComplete} />
              </div>
            )}
            
            {/* Round Instructions Content */}
            <div className="flex flex-col justify-center items-center w-full h-full px-8">
              <div className="text-center max-w-2xl">
                <h1 className="text-4xl font-extrabold text-sleek-700 mb-6 tracking-wide">
                  Round {gameState.currentRound}
                </h1>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">{instructions.title}</h2>
                <p className="text-lg text-gray-700 mb-4 text-center">{instructions.text}</p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left mb-8 max-w-xl w-full">
                  <h3 className="font-bold text-emerald-600 mb-2">Example:</h3>
                  <p className="text-gray-800 text-base">{instructions.example}</p>
                </div>
                <button
                  onClick={handleRoundStartClick}
                  className="bg-gradient-to-r from-sleek-600 to-emerald-600 text-white text-xl px-12 py-4 rounded-full font-bold shadow hover:from-sleek-700 hover:to-emerald-700 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-emerald-300"
                >
                  Start Round
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show scores screen after 3rd round
  if (gameState.showScores) {
    return (
      <div className="w-full h-full bg-white rounded-lg shadow-lg" style={{ width: width, height: height }}>
        {/* Game Board Container - matches main game structure */}
        <div className="bg-gray-50 rounded-lg p-4 shadow-md relative" style={{
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          display: 'block',
          margin: '0 auto'
        }}>
          
          {/* Game Play Area - matches main game structure */}
          <div className="relative w-full h-full" style={{ height: 'calc(100% - 60px)' }}>
            
            {/* Scores Content */}
            <div className="flex flex-col justify-center items-center w-full h-full">
              <div className="text-center max-w-4xl">
                <h1 className="text-4xl font-extrabold text-sleek-700 mb-8 tracking-wide">Game Complete!</h1>
                
                {/* Scores Grid */}
                <div className="grid grid-cols-3 gap-6 mb-8 w-full">
                  {roundMetrics.map((metrics, index) => (
                    <div key={index} className="bg-gradient-to-br from-emerald-50 to-sleek-50 border border-emerald-200 rounded-xl p-6 text-center">
                      <h3 className="text-2xl font-bold text-sleek-700 mb-4">Round {index + 1}</h3>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Accuracy:</span>
                          <span className="font-semibold text-emerald-400">
                            {metrics.totalTrials > 0 ? Math.round((metrics.correctTrials / metrics.totalTrials) * 100) : 0}%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Correct Hits:</span>
                          <span className="font-semibold text-green-600">{metrics.correctTrials}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">False Alarms:</span>
                          <span className="font-semibold text-red-600">{metrics.falsePositives}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Missed Targets:</span>
                          <span className="font-semibold text-amber-600">{metrics.missedTargets}</span>
                        </div>
                        {metrics.reactionTimeValid > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Avg RT:</span>
                            <span className="font-semibold text-sleek-600">{Math.round(metrics.reactionTimeValid)}ms</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="text-center">
                  <p className="text-lg text-gray-600 mb-4">Game completed! Moving to assessment questions...</p>
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show Thank You screen if flagged
  if (gameState.showThankYou) {
    return (
      <div className="w-full h-full bg-white flex items-center justify-center" style={{ width: width, height: height }}>
        <div className="bg-white rounded-2xl shadow-2xl flex flex-col items-center justify-center p-12" style={{ maxWidth: '90%', maxHeight: '90%' }}>
          <h2 className="text-3xl font-extrabold text-sleek-700 mb-6">Thank you!</h2>
          <p className="text-lg text-gray-700 mb-2 text-center">Your responses have been recorded.</p>
          <p className="text-gray-500 text-center">You may now close this window.</p>
        </div>
      </div>
    );
  }

  // Show round instructions
  if (!gameState.isPlaying && !gameState.gameComplete && !gameState.showScores && !gameState.showQuestions) {
    const instructions = getRoundInstructions(gameState.currentRound);
    return (
      <div className="flex items-center justify-center bg-gradient-to-br from-sleek-50 to-emerald-50" style={{ width: width, height: height }}>
        <div className="rounded-2xl border border-gray-200 shadow-2xl bg-white flex flex-col justify-between" style={{ width: width, height: height }}>
          <div className="flex flex-col items-center justify-center h-full px-8">
            <h1 className="text-3xl font-extrabold text-sleek-700 mb-6 tracking-wide">
              {instructions.title}
            </h1>
            <div className="text-lg text-gray-700 space-y-4 max-w-2xl text-center">
              <p className="font-semibold">
                {instructions.text}
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                <h3 className="font-bold text-emerald-600 mb-2">Example:</h3>
                <p className="text-gray-800 text-sm">{instructions.example}</p>
              </div>
            </div>
          </div>
          {/* Start Round Button */}
          <div className="w-full flex justify-center items-center pb-4 pt-2">
            <button
              onClick={onStartRound}
              className="bg-gradient-to-r from-sleek-600 to-emerald-600 text-white text-lg px-10 py-3 rounded-full font-bold shadow hover:from-sleek-700 hover:to-emerald-700 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-emerald-300"
            >
              Start Round
            </button>
          </div>
        </div>
      </div>
    );
  }



  // Show self-report questions - this should be handled by the parent component
  if (gameState.showQuestions) {
    return (
      <div className="w-full h-full bg-gradient-to-br from-sleek-50 to-emerald-50 flex items-center justify-center" style={{ width: width, height: height }}>
        <div className="bg-white rounded-2xl shadow-2xl flex flex-col justify-center items-center p-8" style={{ maxWidth: '90%', maxHeight: '90%' }}>
          <h1 className="text-3xl font-extrabold text-sleek-700 mb-6 tracking-wide">
            Loading Self-Report Questions...
          </h1>
          <p className="text-lg text-gray-600 mb-6 text-center">
            Please wait while we prepare your assessment questions.
          </p>
        </div>
      </div>
    );
  }

  // Show game complete screen (legacy - keeping for compatibility)
  if (gameState.gameComplete && !gameState.showScores && !gameState.showQuestions) {
    return (
      <div className="w-full h-full bg-gradient-to-br from-sleek-50 to-emerald-50 flex items-center justify-center" style={{ width: width, height: height }}>
        <div className="bg-white rounded-2xl shadow-2xl flex flex-col justify-center items-center" style={{ maxWidth: '90%', maxHeight: '90%' }}>
          <h1 className="text-4xl font-extrabold text-sleek-700 mb-4 tracking-wide">
            Game Complete!
          </h1>
          <p className="text-xl text-gray-600 mb-6">
            Thank you for completing the Signal Snap assessment.
          </p>
          <div className="text-center text-gray-700">
            <p>Your results have been recorded.</p>
            <p>You can view your detailed results in the dashboard.</p>
          </div>
        </div>
      </div>
    );
  }

  // Show main game interface
  return (
    <div className="w-full h-full bg-white rounded-lg shadow-lg" style={{ width: width, height: height }}>
      {/* Game Board Container - matches BounceBack structure */}
      <div className="bg-gray-50 rounded-lg p-2 shadow-md relative" style={{
        width: '100%',
        height: '100%',
        maxWidth: '100%',
        maxHeight: '100%',
        display: 'block',
        margin: '0 auto'
      }}>
        
        {/* Game Play Area - matches canvas area structure */}
        <div className="relative w-full h-full" style={{ height: 'calc(100% - 40px)' }}>
          
          {/* Game Content */}
          <div className="flex flex-col justify-between w-full h-full" style={{ overflow: 'hidden' }}>
            {/* Simple Centered Header */}
            <div className="w-full flex justify-between items-center pt-2 pb-1">
              <div style={{ width: 80 }} /> {/* Spacer for left */}
              <div className="flex gap-8 text-lg font-semibold">
                <span className="text-gray-700">Round: <span className="text-sleek-600 font-bold">{gameState.currentRound}/3</span></span>
                <span className="text-gray-700">Trial: <span className="text-emerald-600 font-bold">{gameState.currentTrial}/30</span></span>
              </div>
              <div className="text-right pr-4 min-w-[80px]">
                <span className="text-gray-500 text-base">Timer:</span>
                <span className="ml-2 text-xl font-mono text-gray-800">{elapsedTime.toFixed(1)}s</span>
              </div>
            </div>
            {/* Main Content Row */}
            <div className="flex flex-1 flex-row items-center justify-between px-8 py-2 gap-6">
              {/* Left: Current Target (40%) */}
              <div className="flex flex-col items-center justify-center" style={{ flexBasis: '40%', maxWidth: '40%' }}>
                <h3 className="text-lg font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <svg className="w-5 h-5 text-sleek-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Current Target
                </h3>
                <div className="mb-1">
                  <ShapeRenderer 
                    shape={gameState.currentTarget.shape} 
                    pattern={gameState.currentTarget.pattern} 
                    size="w-24 h-24" 
                  />
                </div>
                <p className="text-center text-gray-800 text-lg font-semibold">
                  {gameState.currentTarget.shape.charAt(0).toUpperCase() + gameState.currentTarget.shape.slice(1)}
                </p>
                <p className="text-center text-emerald-500 text-base font-medium">
                  {gameState.currentTarget.pattern.charAt(0).toUpperCase() + gameState.currentTarget.pattern.slice(1)}
                </p>
              </div>
              {/* Right: Current Stimulus (60%) */}
              <div className="flex flex-col items-center justify-center" style={{ flexBasis: '60%', maxWidth: '60%' }}>
                <h3 className="text-xl font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Current Stimulus
                </h3>
                <div className={`flex justify-center items-center mb-2 transition-all duration-300 ${gameState.showStimulus ? 'animate-pulse' : ''}`} style={{ minHeight: '180px' }}>
                  {gameState.showStimulus && gameState.currentStimulus ? (
                    <button
                      onClick={onStimulusClick}
                      className="cursor-pointer hover:scale-105 transition-transform duration-200 focus:outline-none focus:ring-4 focus:ring-emerald-300"
                      title="Click if this matches the target"
                    >
                      <ShapeRenderer 
                        shape={gameState.currentStimulus.shape} 
                        pattern={gameState.currentStimulus.pattern} 
                        size="w-40 h-40"
                        rotation={stimulusRotation}
                      />
                    </button>
                  ) : (
                    <div className="w-40 h-40 border-4 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 rounded-xl text-2xl text-gray-400 font-semibold">
                      Waiting...
                    </div>
                  )}
                </div>
                {gameState.currentStimulus && (
                  <p className="text-xl text-gray-700 font-bold mb-2 tracking-wide">
                    {gameState.currentStimulus.shape.charAt(0).toUpperCase() + gameState.currentStimulus.shape.slice(1)} + {gameState.currentStimulus.pattern.charAt(0).toUpperCase() + gameState.currentStimulus.pattern.slice(1)}
                  </p>
                )}
              </div>
            </div>
            {/* Add the instruction tile just above the progress bar */}
            <div className="w-full flex justify-center items-center pb-1">
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-0 py-3 shadow text-center w-full">
                <span className="text-base text-gray-700 font-medium">
                  {getInRoundInstruction(gameState.currentRound)}
                </span>
              </div>
            </div>
            {/* Progress Bar (bottom) */}
            <div className="px-8">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Round Progress</span>
                <span>{gameState.currentTrial}/30</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
                <div
                  className="bg-gradient-to-r from-sleek-500 to-emerald-500 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${(gameState.currentTrial / 30) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameUI; 